import { Arcified, ArcifiedTube, ArcifyOperation } from "@oshcut/oshlib"
import { arcifyHttpTransaction } from "arcifyHttpTransaction"
import { reprocessPart, setBendMachineIdSource } from "arcifyRecipes"
import diff from 'deep-diff'
import Log from "logs"
import semverLt from 'semver/functions/lt'
import clone from 'rfdc'
import { isSheetMetal, isTube } from "partTypeGuards"

const MINIMUM_ARCIFIED_VERSION = '4.6.0'

/**
 * Fixes up a part after loading or duplicating. The following operations are conditionally performed on the latest
 * version of the part:
 * - If the part's bendMachineIdSource is "manual", the bendMachineIdSource will be reset to "auto" and the part will be
 *   fully reprocessed.
 * - If the part's version is less than MINIMUM_ARCIFIED_VERSION, the part will be fully reprocessed and its version set
 *   to the latest arcify package version.
 * @param arcified The part to reprocess.
 * @returns The reprocessed part.
 */
export async function fixUpPart(arcified: Arcified | ArcifiedTube) {

  // If the part requires any additional operations, perform them now.
  let operations: ArcifyOperation[] = []
  let description = ''

  if (isSheetMetal(arcified) && arcified.bendMachineIdSource === 'manual') {
    // The customer is not allowed to set this value to "manual". If this value is "manual", it's because an admin set
    // that value, and then that part was duplicated. Reset this value to "auto" and reprocess the part so that when the
    // part is ordered again, it can be made on the correct press brake.
    Log.info('Resetting bendMachineIdSource to "auto" for part ' + arcified.id)
    description = 'Resetting bendMachineIdSource to "auto" and reprocessing part.'
    operations = setBendMachineIdSource('auto')
  } else if (semverLt(arcified.arcifyVersion, MINIMUM_ARCIFIED_VERSION)) {
    // Arcify version is too old, so reprocess the part.
    Log.info('Arcify version ' + arcified.arcifyVersion + ' is too old. Required version is ' + MINIMUM_ARCIFIED_VERSION + '. Reprocessing part.')
    description = 'Reprocessing part.'
    operations = reprocessPart()

    // Re-run auto-detect part type
    operations.splice(0, 0, { type: 'autoDetectPartType', fixUp: true })
  }

  if (operations.length > 0) {
    let saveResult = await arcifyHttpTransaction({
      partId: arcified.id,
      version: 'latest',
      diff: true,
      description,
      operations
    })
    if (saveResult.diff && !saveResult.full) {
      saveResult.full = clone()(arcified)
      saveResult.diff.forEach((record: any) => diff.applyChange(saveResult.full, null, record))
    }
    if (!saveResult.full) throw new Error('Failed to reprocess part ' + arcified.id)
    arcified = saveResult.full
  }

  return arcified
}